import { useQuery } from "@tanstack/react-query";
import { fromTable, supabase } from "@/integrations/supabase/db";

export interface BeratungModul {
  id: string;
  tenant_id: string | null;
  bereich_id: string | null;
  name: string;
  beschreibung: string | null;
  gruppe: 'gruppe1_allgemein' | 'gruppe2_fachbereiche';
  icon: string | null;
  reihenfolge: number;
  kundentypen: string[];
  aktiv: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  bereich_name?: string | null;
}

export interface BeratungSparte {
  id: string;
  modul_id: string;
  sparte_id: string | null;
  name: string | null;
  reihenfolge: number;
  hat_bedarfsanalyse: boolean;
  hat_vergleichsrechner: boolean;
  hat_anlegerprofil: boolean;
  quicklinks: any[];
  aktiv: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  sparte_name?: string | null;
}

interface UseBeratungModuleOptions {
  gruppe?: BeratungModul['gruppe'];
  kundentyp?: string;
  enabled?: boolean;
}

export function useBeratungModule({ gruppe, kundentyp, enabled = true }: UseBeratungModuleOptions = {}) {
  return useQuery({
    queryKey: ['beratung-module', gruppe, kundentyp],
    queryFn: async (): Promise<BeratungModul[]> => {
      // Query crm schema via fromTable helper
      let query = fromTable('beratung_module')
        .select('*')
        .eq('aktiv', true)
        .order('reihenfolge', { ascending: true });

      if (gruppe) {
        query = query.eq('gruppe', gruppe);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      let modules = (data || []) as any[];
      
      // Fetch bereich names for modules with bereich_id
      const bereichIds = modules.map(m => m.bereich_id).filter(Boolean);
      let bereichMap: Record<string, string> = {};
      
      if (bereichIds.length > 0) {
        const { data: bereiche } = await supabase
          .from('bereiche')
          .select('id, name')
          .in('id', bereichIds);
        
        bereichMap = (bereiche || []).reduce((acc, b) => {
          acc[b.id] = b.name;
          return acc;
        }, {} as Record<string, string>);
      }
      
      modules = modules.map(m => ({
        ...m,
        bereich_name: m.bereich_id ? bereichMap[m.bereich_id] || null : null,
      }));
      
      // Filter by kundentyp if provided
      if (kundentyp) {
        modules = modules.filter(m => 
          m.kundentypen?.includes(kundentyp) || !m.kundentypen?.length
        );
      }
      
      return modules as BeratungModul[];
    },
    enabled,
  });
}

interface UseBeratungSpartenOptions {
  modulId?: string;
  enabled?: boolean;
}

export function useBeratungSparten({ modulId, enabled = true }: UseBeratungSpartenOptions = {}) {
  return useQuery({
    queryKey: ['beratung-sparten', modulId],
    queryFn: async (): Promise<BeratungSparte[]> => {
      if (!modulId) return [];
      
      const { data, error } = await fromTable('beratung_sparten')
        .select('*')
        .eq('modul_id', modulId)
        .eq('aktiv', true)
        .order('reihenfolge', { ascending: true });

      if (error) throw error;
      
      let sparten = (data || []) as any[];
      
      // Fetch sparte names for entries with sparte_id
      const sparteIds = sparten.map(s => s.sparte_id).filter(Boolean);
      let sparteMap: Record<string, string> = {};
      
      if (sparteIds.length > 0) {
        const { data: spartenData } = await supabase
          .from('sparten')
          .select('id, sparte')
          .in('id', sparteIds);
        
        sparteMap = (spartenData || []).reduce((acc, s) => {
          acc[s.id] = s.sparte;
          return acc;
        }, {} as Record<string, string>);
      }
      
      return sparten.map(s => ({
        ...s,
        sparte_name: s.sparte_id ? sparteMap[s.sparte_id] || s.name : s.name,
      })) as BeratungSparte[];
    },
    enabled: enabled && !!modulId,
  });
}
